import { useFrame, useThree } from '@react-three/fiber'
import { FC, useEffect, useMemo, useRef } from 'react'
import { Vector3 } from 'three'

const MOVEMENT_KEYS = new Set([
  'w',
  's',
  'a',
  'd',
  'q',
  'e',
  'arrowup',
  'arrowdown',
  'arrowleft',
  'arrowright',
  ' ',
  'shift',
])

interface KeyboardFlyControlsProps {
  speed?: number
  isActive?: boolean
}

/**
 * Keyboard flying controls - WASD/Arrow keys to move camera.
 * Only captures input when isActive is true.
 */
export const KeyboardFlyControls: FC<KeyboardFlyControlsProps> = ({
  speed = 2,
  isActive = true,
}) => {
  const { camera, controls } = useThree()
  const keysPressed = useRef<Set<string>>(new Set())

  // Reusable vectors for frame loop calculations
  const tempVectors = useMemo(
    () => ({
      forward: new Vector3(),
      right: new Vector3(),
      movement: new Vector3(),
      temp: new Vector3(),
    }),
    []
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only capture when viewer is active
      if (!isActive) return

      // Don't capture if typing in an input
      const target = e.target as HTMLElement
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return
      }

      const key = e.key.toLowerCase()

      // Prevent default for movement keys (e.g., SPACE scrolling)
      if (MOVEMENT_KEYS.has(key)) {
        e.preventDefault()
      }

      keysPressed.current.add(key)
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      keysPressed.current.delete(e.key.toLowerCase())
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [isActive])

  useFrame((_, delta) => {
    const keys = keysPressed.current
    if (keys.size === 0) return

    const moveSpeed = speed * delta * 60
    const { forward, right, movement, temp } = tempVectors

    // Get camera direction vectors
    camera.getWorldDirection(forward)
    right.crossVectors(forward, camera.up).normalize()
    movement.set(0, 0, 0)

    // WASD + Arrow keys
    if (keys.has('w') || keys.has('arrowup')) {
      temp.copy(forward).multiplyScalar(moveSpeed)
      movement.add(temp)
    }
    if (keys.has('s') || keys.has('arrowdown')) {
      temp.copy(forward).multiplyScalar(-moveSpeed)
      movement.add(temp)
    }
    if (keys.has('a') || keys.has('arrowleft')) {
      temp.copy(right).multiplyScalar(-moveSpeed)
      movement.add(temp)
    }
    if (keys.has('d') || keys.has('arrowright')) {
      temp.copy(right).multiplyScalar(moveSpeed)
      movement.add(temp)
    }
    // Q/E/Shift/Space for up/down
    if (keys.has('q') || keys.has('shift')) {
      movement.y -= moveSpeed
    }
    if (keys.has('e') || keys.has(' ')) {
      movement.y += moveSpeed
    }

    if (movement.length() > 0) {
      camera.position.add(movement)

      // Update OrbitControls target to move with camera
      if (controls && 'target' in controls && 'update' in controls) {
        const orbitControls = controls as unknown as {
          target: Vector3
          update: () => void
        }
        orbitControls.target.add(movement)
        orbitControls.update()
      }
    }
  })

  return null
}
